import { HeatmapChartConfigDto, HeatmapChartDataDto, PlayerStat } from 'types/CodegenReportTypes';
import { ChartProps } from 'utilities/chartIdToComponent';
import StrikeZoneHeatmap from 'components/StrikeZoneHeatmap/StrikeZoneHeatmap';
import { useMemo } from 'react';
import { useGetUnitSystem } from 'hooks/helperHooks';
import usePlayerReportBatter from 'hooks/PlayerReport/Batter/usePlayerReportBatter';
import usePlayerReportPitcher from 'hooks/PlayerReport/Pitcher/usePlayerReportPitcher';
import useSessionReportBatter from 'hooks/SessionReport/Batter/useSessionReportBatter';
import useSessionReportPitcher from 'hooks/SessionReport/Pitcher/useSessionReportPitcher';
import useSingleSessionReportPitcher from 'hooks/SessionReport/PitcherSingle/useSingleSessionReportPitcher';

type Props = ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto> & {
  playerType: 'Batter' | 'Pitcher';
  hasConfigEdits: boolean;
};

const formatValueMap = new Map<PlayerStat | 'PitchLocation', (value: number | null) => string>([
  ['PitchLocation', (value: number | null) => (value != null ? value.toFixed(1) + '%' : '-')],
  ['ExitSpeed', (value: number | null) => (value != null ? value.toFixed(1) : '-')],
  ['BattingAverage', (value: number | null) => (value != null ? value.toFixed(3).replace('0.', '.') : '-')],
  ['Slugging', (value: number | null) => (value != null ? value.toFixed(3).replace('0.', '.') : '-')],
]);

const StrikeZoneHeatmapChart = ({ chartData, isLoading, extraChartData, playerType, hasConfigEdits }: Props) => {
  const unitSystem = useGetUnitSystem();

  const formatValue = useMemo(
    () =>
      formatValueMap.get(extraChartData.viewDataAs === 'PitchLocation' ? 'PitchLocation' : extraChartData.playerStat) ||
      ((value: number | null) => (value != null ? value.toFixed(1) : '-')),
    [extraChartData]
  );

  const scale = useMemo(() => {
    if (extraChartData.viewDataAs === 'PitchLocation') return [0, 10];

    switch (extraChartData.playerStat) {
      case 'ExitSpeed':
        return unitSystem === 'Metric' ? [110, 150] : [70, 95];
      case 'BattingAverage':
        return [0.175, 0.3];
      case 'Slugging':
        return [0.3, 0.6];
      default:
        return [0, 1];
    }
  }, [extraChartData, unitSystem]);

  return (
    <StrikeZoneHeatmap
      chartData={hasConfigEdits ? undefined : chartData}
      isLoading={isLoading}
      playerType={playerType}
      formatValue={formatValue}
      scaleMin={scale[0]}
      scaleMax={scale[1]}
    />
  );
};

export const PitcherStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  const { layout } = usePlayerReportPitcher();
  const hasConfigEdits = useMemo(
    () =>
      layout?.pitcherHeatmapChartConfig?.viewDataAs !== props.extraChartData.viewDataAs ||
      layout?.pitcherHeatmapChartConfig?.playerStat !== props.extraChartData.playerStat,
    [layout?.pitcherHeatmapChartConfig, props.extraChartData]
  );

  return <StrikeZoneHeatmapChart {...props} playerType={'Pitcher'} hasConfigEdits={hasConfigEdits} />;
};

export const PitcherSessionStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  const { layout } = useSessionReportPitcher();
  const hasConfigEdits = useMemo(
    () =>
      layout?.pitcherSessionHeatmapChartConfig?.viewDataAs !== props.extraChartData.viewDataAs ||
      layout?.pitcherSessionHeatmapChartConfig?.playerStat !== props.extraChartData.playerStat,
    [layout?.pitcherSessionHeatmapChartConfig, props.extraChartData]
  );

  return <StrikeZoneHeatmapChart {...props} playerType={'Pitcher'} hasConfigEdits={hasConfigEdits} />;
};

export const PitcherSingleSessionStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  const { layout } = useSingleSessionReportPitcher();
  const hasConfigEdits = useMemo(
    () =>
      layout?.pitcherSingleSessionHeatmapChartConfig?.viewDataAs !== props.extraChartData.viewDataAs ||
      layout?.pitcherSingleSessionHeatmapChartConfig?.playerStat !== props.extraChartData.playerStat,
    [layout?.pitcherSingleSessionHeatmapChartConfig, props.extraChartData]
  );

  return <StrikeZoneHeatmapChart {...props} playerType={'Pitcher'} hasConfigEdits={hasConfigEdits} />;
};

export const BatterStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  const { layout } = usePlayerReportBatter();
  const hasConfigEdits = useMemo(
    () =>
      layout?.batterHeatmapChartConfig?.viewDataAs !== props.extraChartData.viewDataAs ||
      layout?.batterHeatmapChartConfig?.playerStat !== props.extraChartData.playerStat,
    [layout?.batterHeatmapChartConfig, props.extraChartData]
  );

  return <StrikeZoneHeatmapChart {...props} playerType={'Batter'} hasConfigEdits={hasConfigEdits} />;
};

export const BatterSessionStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  const { layout } = useSessionReportBatter();
  const hasConfigEdits = useMemo(
    () =>
      layout?.batterSessionHeatmapChartConfig?.viewDataAs !== props.extraChartData.viewDataAs ||
      layout?.batterSessionHeatmapChartConfig?.playerStat !== props.extraChartData.playerStat,
    [layout?.batterSessionHeatmapChartConfig, props.extraChartData]
  );

  return <StrikeZoneHeatmapChart {...props} playerType={'Batter'} hasConfigEdits={hasConfigEdits} />;
};
